import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTeam } from '../../../context/TeamContext';
import { EQUIPMENT_TYPES } from '../../../data/nurseAppModels';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { INSPECTION_ROUNDS } from '../../../data/nurseAppModels';

const CompareInspectionsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentTeam } = useTeam();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInspections, setSelectedInspections] = useState([]);
  const [showDifferencesOnly, setShowDifferencesOnly] = useState(false);

  const round = searchParams.get('round');
  const dateParam = searchParams.get('date');

  useEffect(() => {
    const loadInspections = async () => {
      if (!currentTeam || !round) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const inspectionsQuery = query(
          collection(db, 'equipmentInspections'),
          where('teamId', '==', currentTeam.id),
          where('round', '==', round)
        );

        const snapshot = await getDocs(inspectionsQuery);
        let inspectionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Filter by date if provided
        if (dateParam) {
          const targetDate = new Date(dateParam);
          inspectionsData = inspectionsData.filter(inspection => {
            const inspectionDate = inspection.date?.toDate?.() || new Date(inspection.date);
            return inspectionDate.toDateString() === targetDate.toDateString();
          });
        }

        // Sort by submittedAt or createdAt
        inspectionsData.sort((a, b) => {
          const dateA = a.submittedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.submittedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA;
        });

        setInspections(inspectionsData);
        setSelectedInspections(inspectionsData.slice(0, 3).map(i => i.id)); // Select first 3 by default
      } catch (err) {
        console.error('Error loading inspections:', err);
      } finally {
        setLoading(false);
      }
    };

    loadInspections();
  }, [currentTeam, round, dateParam]);

  const getRoundLabel = (round) => {
    const rounds = {
      [INSPECTION_ROUNDS.MORNING_BEFORE]: 'เช้า - ก่อนเข้าเวร',
      [INSPECTION_ROUNDS.MORNING_AFTER]: 'เช้า - หลังเข้าเวร',
      [INSPECTION_ROUNDS.AFTERNOON_BEFORE]: 'บ่าย - ก่อนเข้าเวร',
      [INSPECTION_ROUNDS.AFTERNOON_AFTER]: 'บ่าย - หลังเข้าเวร',
      [INSPECTION_ROUNDS.NIGHT_BEFORE]: 'ดึก - ก่อนเข้าเวร',
      [INSPECTION_ROUNDS.NIGHT_AFTER]: 'ดึก - หลังเข้าเวร'
    };
    return rounds[round] || round;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get all equipment IDs from selected inspections
  const getAllEquipmentIds = () => {
    const equipmentIds = new Set();
    selectedInspections.forEach(inspectionId => {
      const inspection = inspections.find(i => i.id === inspectionId);
      if (inspection?.items) {
        inspection.items.forEach(item => {
          equipmentIds.add(item.equipmentId);
        });
      }
    });
    return Array.from(equipmentIds);
  };

  // Check if equipment has differences
  const hasDifferences = (equipmentId) => {
    const values = selectedInspections.map(inspectionId => {
      const inspection = inspections.find(i => i.id === inspectionId);
      const item = inspection?.items?.find(i => i.equipmentId === equipmentId);
      return item;
    }).filter(Boolean);

    if (values.length < 2) return false;

    // Check small items differences
    const firstItem = values[0];
    if (firstItem.type === EQUIPMENT_TYPES.SMALL_ITEM) {
      return values.some(item => 
        item.availableQuantity !== firstItem.availableQuantity ||
        item.damagedQuantity !== firstItem.damagedQuantity ||
        item.lostQuantity !== firstItem.lostQuantity
      );
    }

    // Check large items differences
    return values.some(item => item.status !== firstItem.status);
  };

  const selectedInspectionsData = inspections.filter(i => selectedInspections.includes(i.id));
  const equipmentIds = getAllEquipmentIds();
  const filteredEquipmentIds = showDifferencesOnly
    ? equipmentIds.filter(id => hasDifferences(id))
    : equipmentIds;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </div>
    );
  }

  if (inspections.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <h3 className="text-xl font-bold text-gray-800 mb-2">ไม่พบรายการตรวจสอบ</h3>
            <button
              onClick={() => navigate('/nurse/equipment/inspections/approval')}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              กลับ
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/equipment/inspections/approval')}
            className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
          >
            ← กลับ
          </button>
          <h1 className="text-2xl font-bold text-gray-800">เปรียบเทียบผลการตรวจสอบ</h1>
          <p className="text-gray-600 mt-1">
            {getRoundLabel(round)} - {dateParam ? formatDate(new Date(dateParam)) : 'ทั้งหมด'}
          </p>
        </div>

        {/* Select Inspections */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">เลือกรายการที่ต้องการเปรียบเทียบ</h2>
          <div className="space-y-2">
            {inspections.map(inspection => (
              <label key={inspection.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedInspections.includes(inspection.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      if (selectedInspections.length < 3) {
                        setSelectedInspections([...selectedInspections, inspection.id]);
                      } else {
                        alert('สามารถเปรียบเทียบได้สูงสุด 3 รายการ');
                      }
                    } else {
                      setSelectedInspections(selectedInspections.filter(id => id !== inspection.id));
                    }
                  }}
                  className="w-4 h-4"
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{inspection.inspectedByName || '-'}</p>
                  <p className="text-sm text-gray-500">{formatDate(inspection.submittedAt || inspection.createdAt)}</p>
                </div>
                <span className={`px-3 py-1 rounded-lg text-sm ${
                  inspection.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                  inspection.status === 'PENDING_APPROVAL' ? 'bg-yellow-100 text-yellow-800' :
                  inspection.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {inspection.status === 'APPROVED' ? '✅' :
                   inspection.status === 'PENDING_APPROVAL' ? '⏳' :
                   inspection.status === 'REJECTED' ? '❌' : '📝'}
                </span>
              </label>
            ))}
          </div>
          {selectedInspections.length === 0 && (
            <p className="text-sm text-gray-500 mt-4">กรุณาเลือกรายการอย่างน้อย 2 รายการ</p>
          )}
        </div>

        {/* Comparison Table */}
        {selectedInspections.length >= 2 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-800">ตารางเปรียบเทียบ</h2>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showDifferencesOnly}
                  onChange={(e) => setShowDifferencesOnly(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-700">แสดงเฉพาะรายการที่มีความแตกต่าง</span>
              </label>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 sticky left-0 bg-white z-10">
                      อุปกรณ์
                    </th>
                    {selectedInspectionsData.map(inspection => (
                      <th key={inspection.id} className="text-center py-2 px-3 text-sm font-semibold text-gray-700 min-w-[150px]">
                        <div>
                          <p className="font-medium">{inspection.inspectedByName || '-'}</p>
                          <p className="text-xs text-gray-500">{formatDate(inspection.submittedAt || inspection.createdAt)}</p>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredEquipmentIds.map(equipmentId => {
                    const firstInspection = selectedInspectionsData[0];
                    const firstItem = firstInspection?.items?.find(i => i.equipmentId === equipmentId);
                    if (!firstItem) return null;

                    const isSmallItem = firstItem.type === EQUIPMENT_TYPES.SMALL_ITEM;
                    const hasDiff = hasDifferences(equipmentId);

                    return (
                      <tr key={equipmentId} className={`border-b ${hasDiff ? 'bg-yellow-50' : ''}`}>
                        <td className="py-2 px-3 sticky left-0 bg-white z-10">
                          <div>
                            <p className="font-medium text-gray-800">{firstItem.equipmentName}</p>
                            <p className="text-xs text-gray-500">{firstItem.equipmentCode}</p>
                          </div>
                        </td>
                        {selectedInspectionsData.map(inspection => {
                          const item = inspection.items?.find(i => i.equipmentId === equipmentId);
                          if (!item) {
                            return (
                              <td key={inspection.id} className="text-center py-2 px-3 text-gray-400">
                                -
                              </td>
                            );
                          }

                          const isDifferent = hasDiff && (
                            item.availableQuantity !== firstItem.availableQuantity ||
                            item.damagedQuantity !== firstItem.damagedQuantity ||
                            item.lostQuantity !== firstItem.lostQuantity ||
                            item.status !== firstItem.status
                          );

                          return (
                            <td key={inspection.id} className={`text-center py-2 px-3 ${isDifferent ? 'bg-yellow-100 font-semibold' : ''}`}>
                              {isSmallItem ? (
                                <div className="space-y-1">
                                  <div>
                                    <span className="text-xs text-gray-500">Stock ก่อนหน้า: </span>
                                    <span className="font-medium">{item.previousStock || 0}</span>
                                  </div>
                                  <div>
                                    <span className="text-xs text-gray-500">คงเหลือ: </span>
                                    <span className={`font-medium ${isDifferent ? 'text-red-600' : ''}`}>
                                      {item.availableQuantity || 0}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-xs text-gray-500">เสียหาย: </span>
                                    <span className={`font-medium ${isDifferent ? 'text-red-600' : ''}`}>
                                      {item.damagedQuantity || 0}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-xs text-gray-500">สูญหาย: </span>
                                    <span className={`font-medium ${isDifferent ? 'text-red-600' : ''}`}>
                                      {item.lostQuantity || 0}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <span className={`px-2 py-1 rounded text-sm ${
                                    item.status === 'AVAILABLE' ? 'bg-green-100 text-green-800' :
                                    item.status === 'BORROWED' ? 'bg-yellow-100 text-yellow-800' :
                                    item.status === 'REPAIR' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {item.status === 'AVAILABLE' ? 'พร้อมใช้' :
                                     item.status === 'BORROWED' ? 'ถูกยืม' :
                                     item.status === 'REPAIR' ? 'อยู่ระหว่างส่งซ่อม' : item.status}
                                  </span>
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredEquipmentIds.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {showDifferencesOnly 
                  ? 'ไม่พบรายการที่มีความแตกต่าง' 
                  : 'ไม่พบข้อมูลอุปกรณ์'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompareInspectionsPage;

